import {MenuBar, SkipToSection} from '@mvps-genesis/accessibility';
import {PageManager} from '@mvps-genesis/bootstrap';
import StorageFactory from '@mvps-genesis/storage-factory';
import 'construct-style-sheets-polyfill';
import Bloomreach from 'custom-extensions/bloomreach';
import 'custom-extensions/bopis/lib/BopisElements';
import MiniBasket from 'custom-extensions/mini-basket';
import {Autocomplete} from 'custom-extensions/search';
import {initCarousels} from 'extensions/carousel';
import ConfirmAction from 'extensions/confirm-action';
import ContinueShopping from 'extensions/continue-shopping';
import dynamicFormElementAttributes from 'extensions/dynamic-form-element-attributes';
import {GlobalMessageContainer} from 'extensions/messages';
import NavigationMenu from 'extensions/navigation-menu';
import './Global.scss';

export default class Global extends PageManager {

	// eslint-disable-next-line max-lines-per-function
	constructor (pageContext) {

		super(pageContext);

		this.initSkipToSection();
		this.initSearchPreview();

		/**
		 * Create global message container
		 */
		new GlobalMessageContainer({container: '#js-messages'});

		/**
		 * Initialize carousels
		 */
		initCarousels();

		this.initializeHTMLFormValidator();

		/**
		 * Initialize the Dynamic Form-Element Attributes
		 */
		dynamicFormElementAttributes();

		this.initModals();
		this.initGModals();
		this.initLazyload();
		this.initAddressEvents();
		this.initScrollTo();
		this.initBackToTop();
		this.initStickyListener();
		this.initAjaxLoginRegister();
		this.initFormLoadingUI();
		this.initTooltip();
		this.initTabbedCarousels();
		this.initGTM();
		this.initReadMore();
		this.initImageSwap();
		this.initEmailValidationCheck();
		this.initTicker();
	}

	// eslint-disable-next-line max-lines-per-function
	onReady () {

		/**
		 * Initialize the Navigation Menu extension
		 */
		const mainMenuElem = document.querySelector('.js-navigation-menu');
		if (mainMenuElem) {
			new NavigationMenu({
				drawers: '.js-navigation-menu__drawer',
				expandButtons: '.js-navigation-menu__expand',
				mainMenu: mainMenuElem,
				mainMenuCloseButtons: '.js-navigation-menu__close',
				mainMenuOpenButtons: '.js-navigation-menu__open',
				menuItems: '.js-navigation-menu__link',
				parentMenus: '.js-navigation-menu__has-child',
				showPreviousMenuButtons: '.js-navigation-menu__previous'
			});
		}

		const accountLinks = document.getElementById('js-customer-account-links');
		if (accountLinks) {
			// Basic instance constructor
			new MenuBar({
				breakpoint: false,
				element: accountLinks,
				navigateOnDown: true,
				navigateOnEnter: true,
				navigateOnSpace: true
			});
		}

		const staticLinks = document.getElementById('js-static-page-links');
		if (staticLinks) {
			// Basic instance constructor
			new MenuBar({
				element: staticLinks,
				navigateOnDown: true,
				navigateOnEnter: true,
				navigateOnSpace: true
			});
		}

		/**
		 * Initialize Ketch
		 */
		if (window.ketch) {
			this.initKetch();
		}

		/**
		 * Initialize the Bloomreach extension
		 */
		this.initBloomreach();

		/**
		 * Initialize the Mini-Basket extension
		 */
		this.initMiniBasket();

		/**
		 * Initialize continue shopping link
		 */
		new ContinueShopping();
		this.searchFormEvents();
		this.triggerPrint();
		this.initCollapsibleMenu();

		new ConfirmAction();

	}

	initKetch () {
		this.restoreKetchConsentCookies();

		window.ketch('on', 'regionInfo', regionInfo => {
			var customTextRegions = ['US-CA'];
			if (customTextRegions.includes(regionInfo)) {
				var preferenceCenterLinkElement = document.getElementById('ketchPrefCenterLink');

				if (preferenceCenterLinkElement) {
					preferenceCenterLinkElement.textContent = 'Do Not Sell My Personal Information';
				}
			}
		});

		window.ketch('on', 'consent', consent => {
			const analyticsIsGranted = consent?.purposes?.analytics;
			const advertisingIsGranted = consent?.purposes?.targeted_advertising;
			const analyticsConsentValue = analyticsIsGranted ? 'granted' : 'denied';
			const advertisingConsentValue = advertisingIsGranted ? 'granted' : 'denied';

			document.cookie = `mm5-rh-ketch-analytics-consent=${analyticsConsentValue}; path=/; max-age=31536000; SameSite=Lax`;
			document.cookie = `mm5-rh-ketch-advertising-consent=${advertisingConsentValue}; path=/; max-age=31536000; SameSite=Lax`;

			// Mirror to localStorage so the consent value survives a cleared cookie
			StorageFactory.set('mm5-rh-ketch-analytics-consent', analyticsConsentValue);
			StorageFactory.set('mm5-rh-ketch-advertising-consent', advertisingConsentValue);
		});
	}

	/**
	 * Ketch only fires the 'consent' event when consent is granted/updated through
	 * its UI, not on every page load. If the cookie is missing (e.g. cleared by the
	 * browser or user) but we previously stored the value in localStorage, restore
	 * the cookie so server-side logic (e.g. INVC's Bloomreach suppression) keeps working.
	 */
	restoreKetchConsentCookies () {
		const consentCookieNames = ['mm5-rh-ketch-analytics-consent', 'mm5-rh-ketch-advertising-consent'];

		for (const cookieName of consentCookieNames) {
			if (document.cookie.indexOf(`${cookieName}=`) !== -1) {
				continue;
			}

			const storedConsentValue = StorageFactory.get(cookieName);

			if (storedConsentValue) {
				document.cookie = `${cookieName}=${storedConsentValue}; path=/; max-age=31536000; SameSite=Lax`;
			}
		}
	}

	initBloomreach () {
		const options = {
			customerEmail: this.pageContext.Customer_Email?.toLowerCase().trim() ?? '',
			customerID: this.pageContext.Customer_ID ?? ''
		};

		/**
		 * Bloomreach JavaScript integration.
		 * Note: This should be loaded before the MiniBasket extension to avoid
		 * issue with mini basket elements being replaced.
		 */
		new Bloomreach(options);
	}

	initMiniBasket () {
		const miniBasketElement = document.getElementById('js-mini-basket');
		if (!miniBasketElement) {
			return;
		}

		new MiniBasket({
			openOnAddToCart: true
		});
	}

	initSkipToSection () {
		const sections = [];

		const menuElem = document.querySelector('.js-navigation-menu');

		if (menuElem) {
			sections.push({
				label: 'Menu',
				targetElement: menuElem
			});
		}

		const searchElem = document.querySelector('[itemprop="query-input"]');

		if (searchElem) {
			sections.push({
				label: 'Search',
				targetElement: searchElem
			});
		}

		const facetsElem = document.getElementById('js-facets');

		if (facetsElem) {
			sections.push({
				label: 'Filters',
				targetElement: facetsElem
			});
		}

		sections.push({
			label: 'Content',
			targetElement: '.g-main-content-element'
		});

		const accessibilityStatementLink = document.querySelector('.js-disclaimer-link__ACCESS');
		if (accessibilityStatementLink) {
			sections.push({
				label: 'Accessibility Statement',
				targetElement: accessibilityStatementLink
			});
		}

		sections.push({
			label: 'Footer',
			targetElement: '.g-site-footer'
		});

		SkipToSection.createFromArray(sections);
	}

	initSearchPreview () {
		Autocomplete.create({
			defaultAutocompleteParams: {
				includedFacets: [
					'player_name',
					'search_department',
					'real_team'
				],
				resultsPerPage: 5
			},
			defaultSearchParams: {
				resultsPerPage: 5
			},
			searchBarElements: '#js-search-form-input',
			searchButtonElements: '.g-site-header__search-button'
		});
	}

	async initLazyload () {
		// Elements
		const lazyLoadElements = document.getElementsByClassName('lazyload');

		if (lazyLoadElements.length === 0) {
			return;
		}

		// Module
		await import('lazysizes/plugins/unveilhooks/ls.unveilhooks');
		await import('lazysizes/plugins/bgset/ls.bgset');
		await import('lazysizes/plugins/custommedia/ls.custommedia');
		await import('lazysizes');
	}

	/* eslint-disable max-lines-per-function */
	async initCookieConsent () {
		const cookieName = `mm5-${this.pageContext.Store_Code}-genesis-cookie-consent`;

		// Skip if this is a QuickAdd (Fans also Liked) modal
		if (document.cookie.indexOf(cookieName) !== -1 || window.mivaJS.QuickAdd === '1') {
			return;
		}

		const {CookieConsent} = await import('extensions/cookie-consent');

		let contentLink = 'https://www.cookiesandyou.com/';
		let contentLinkTarget = '_blank';

		if (this.pageContext.Privacy_Policy_Url) {
			contentLink = this.pageContext.Privacy_Policy_Url;
			contentLinkTarget = '';
		}

		const removePadding = () => {
			document.body.style.paddingBottom = '';
		};

		new CookieConsent({
			content: {
				dismiss: 'Got it!',
				href: contentLink,
				link: 'Learn More',
				message: 'This website uses cookies to ensure you get the best experience on our website.',
				target: contentLinkTarget
			},
			cookie: {
				name: cookieName,
				secure: true
			},

			onInitialise: (status) => {
				if (status) {
					removePadding();
				}
			},


			onPopupClose: () => {
				removePadding();
			},
			onPopupOpen: () => {
				const banner = document.querySelector('.cc-window');

				if (banner) {
					document.body.style.paddingBottom = `${banner.offsetHeight}px`;
				}

				const dismissBtn = document.querySelector('.cc-dismiss');

				if (dismissBtn) {
					dismissBtn.setAttribute('aria-label', 'Got it! Dismiss cookie message');
				}
			},
			onStatusChange: () => {
				removePadding();
			},
			palette: {
				button: {
					background: '#F9F9F9',
					text: '#000000'
				},
				popup: {
					background: '#222222',
					text: '#FFFFFF'
				}
			}
		});
	}
	/* eslint-enable max-lines-per-function */

	async initializeHTMLFormValidator () {
		const forms = document.getElementsByClassName('js-form-validator');

		if (forms.length === 0) {
			return;
		}

		const {HTMLFormValidator} = await import('extensions/form-validation');

		for (const form of forms) {
			form.HTMLFormValidator = new HTMLFormValidator({
				fieldContainer: '.g-form-list__item',
				formElement: form,
				onValidate: (validator) => {

					/* This fixes an issue where Safari can't handle the regex in the HTML5 Pattern */
					const addressPattern = /^(?!\d+$)[A-Za-z0-9#.,\-\s]+$/;
					const emailDotsPattern = /(^[.])|([.]@)|(\.\.)|(@[.])|(@.*\.$)/;
					let validated = true;
					for (const element of validator.validateElements) {
						if (element.classList.contains('js-address-field') && element?.offsetParent !== null) {
							element.setCustomValidity('');
							if (!addressPattern.test(element.value)) {
								element.setCustomValidity('Invalid');
								validated = false;
							}
						}

						/*
						 * Validate that any emails don't have extra periods
						 * in the wrong places
						 */
						if (element.type === 'email') {
							element.setCustomValidity('');
							if (emailDotsPattern.test(element.value)) {
								element.setCustomValidity('Invalid');
								validated = false;
							}
						}
					}

					return validated;
				}
			});
		}
	}

	async initModals () {
		const hasModalTrigger = document.querySelector(`[data-micromodal-trigger]`);

		if (!hasModalTrigger) {
			return;
		}

		const {default: Modal} = await import('extensions/modal');
		const modal = new Modal();

		/**
		 * Initialize modals:
		 * 	Pass list of modal ids without an overlay that should close when
		 * 	clicking off of modal.
		 */
		modal.init(['js-global-account']);
	}

	async initGModals () {
		const elements = document.querySelectorAll('g-modal');

		if (!elements.length > 0) {
			return;
		}

		await import('custom-extensions/g-modal');
	}

	async initAddressEvents () {
		const datalists = document.querySelectorAll('[data-address-datalist]');
		const toggles = document.querySelectorAll('[data-address-toggle]');

		if (datalists.length === 0 && toggles.length === 0) {
			return;
		}

		const {AddressEvents} = await import('extensions/address-events');

		new AddressEvents(datalists, toggles);
	}

	searchFormEvents () {
		const searchForm = document.getElementById('js-search-form');
		const searchInput = document.getElementById('js-search-form-input');

		if (!searchForm || !searchInput) {
			return;
		}

		const searchClearButton = document.getElementById('js-site-header__search-clear');

		if (searchClearButton) {
			const clearButtonToggleClass = 'is-visible';

			searchClearButton.addEventListener('click', () => {
				searchInput.value = '';
				searchClearButton.classList.remove(clearButtonToggleClass);
				searchClearButton.setAttribute('aria-hidden', 'true');
			});

			searchInput.addEventListener('input', () => {
				searchClearButton.classList.toggle(clearButtonToggleClass, searchInput.value);
				if (searchClearButton.classList.contains(clearButtonToggleClass)) {
					searchClearButton.setAttribute('aria-hidden', 'false');
				} else {
					searchClearButton.setAttribute('aria-hidden', 'true');
				}
			});
		}
	}

	triggerPrint () {

		/**
		 *  Launch Printer Dialog
		 */
		const printTriggers = document.getElementsByClassName('js-trigger-print');

		if (printTriggers.length) {
			for (const element of printTriggers) {
				element.addEventListener('click', function (e) {
					e.preventDefault();
					window.print();
				}, false);
			}
		}

	}

	async initScrollTo () {
		const triggerElements = document.getElementsByClassName('js-scroll-to');

		if (!triggerElements.length) {
			return;
		}

		const scrollTarget = triggerElements[0].getAttribute('data-scroll-to');

		if (!document.querySelector(scrollTarget)) {
			return;
		}

		const {default: ScrollTo} = await import('extensions/scroll-to');

		new ScrollTo(triggerElements, {
			scrollIntoViewOptions: {
				behavior: 'smooth',
				block: 'end',
				inline: 'nearest'
			}
		});
	}

	async initBackToTop () {

		const backToTopButton = document.getElementById('js-back-to-top-button');

		if (!backToTopButton) {
			return;
		}

		const backToTopClass = 'g-back-to-top-button--shown';

		const {default: ScrollTo} = await import('extensions/scroll-to');

		new ScrollTo('#js-back-to-top-button', {
			scrollIntoViewOptions: {
				behavior: 'smooth',
				inline: 'nearest'
			}
		});

		let lastScrollTop = 0;

		window.addEventListener('scroll', () => {
			this.showHideBackToTop(backToTopButton, backToTopClass);

			// Detect if user is scrolling up or down
			let st = window.pageYOffset || document.documentElement.scrollTop;
			if (st > lastScrollTop) {
				document.body.classList.remove('g-user-scrolling-up');
			} else {
				document.body.classList.add('g-user-scrolling-up');
			}

			lastScrollTop = st <= 0 ? 0 : st;
		});

		this.showHideBackToTop(backToTopButton, backToTopClass);

	}

	showHideBackToTop (backToTopButton, backToTopClass) {

		const heightToShowButton = window.innerHeight;
		const offset = heightToShowButton / 2;
		const windowOffset = offset + (isNaN(window.pageYOffset) ? 0 : window.pageYOffset);

		if (windowOffset >= heightToShowButton) {
			backToTopButton.classList.add(backToTopClass);
		} else {
			backToTopButton.classList.remove(backToTopClass);
		}

	}

	initStickyListener () {
		if (!('IntersectionObserver' in window)) {
			return;
		}

		const header = document.querySelector('.g-site-header:not(.g-site-header__unsticky)');

		if (!header) {
			return;
		}

		const observer = new IntersectionObserver(
			([e]) => e.target.classList.toggle('g-site-header__sticky', e.intersectionRatio < 1),
			{threshold: [1]}
		);

		observer.observe(header);
	}

	async initAjaxLoginRegister () {
		const loginRegisterElement = document.getElementById('js-login-register');

		if (!loginRegisterElement) {
			return;
		}

		const {AjaxLoginRegister} = await import('extensions/ajax-login-register');

		new AjaxLoginRegister(loginRegisterElement);
	}

	async initFormLoadingUI () {
		const formElements = document.getElementsByClassName('js-form-show-loading');

		if (!formElements.length) {
			return;
		}

		const {default: LoadingUI} = await import('extensions/loading-ui');
		const loadingContainerSelector = '.js-loading-container';
		const loadingElementSelector = '.js-loading-element';

		for (const formElement of formElements) {
			const loadingContainerElement = formElement.closest(loadingContainerSelector);

			if (!loadingContainerElement) {
				continue;
			}

			const loadingElement = loadingContainerElement.querySelector(loadingElementSelector);

			if (!loadingElement) {
				continue;
			}

			formElement.LoadingUI = new LoadingUI(loadingElement, {
				overlayElement: loadingContainerElement.hasAttribute('data-disable-loading-overlay') ? false : loadingContainerElement
			});

			formElement.addEventListener('submit', () => {
				formElement.LoadingUI.show();
			});
		}
	}

	async initTooltip () {

		const tooltipElements = document.getElementsByClassName('js-tooltip');

		if (!tooltipElements.length) {
			return;
		}

		const {default: Tooltip} = await import('extensions/tooltip');

		for (const element of tooltipElements) {
			new Tooltip(element);
		}
	}

	async initCollapsibleMenu () {

		const menuElements = document.getElementsByClassName('js-collapsible-menu');

		if (menuElements.length === 0) {
			return;
		}

		const {default: CollapsibleMenu} = await import('extensions/collapsible-menu');

		new CollapsibleMenu(menuElements);

	}

	async initTabbedCarousels () {

		const tabbedCarouselElements = document.getElementsByClassName('js-tabbed-carousel');

		if (!tabbedCarouselElements.length) {
			return;
		}

		const {default: TabbedCarousels} = await import('extensions/tabbed-carousels');

		new TabbedCarousels(tabbedCarouselElements);

	}

	async initGTM () {

		const {default: GTM} = await import('extensions/gtm');

		const gtm = new GTM(this.pageContext);
		gtm.init();

		// Listen for Print Events
		window.addEventListener('beforeprint', () => {
			gtm.sendEvent({
				action: 'Click',
				category: 'Print',
				label: document.title,
				noninteractionHit: false
			});
		});
	}

	async initReadMore () {
		// Elements
		const readMoreElements = document.getElementsByClassName('g-read-more');

		if (readMoreElements.length === 0) {
			return;
		}

		// Module
		const {default: ReadMore} = await import('custom-extensions/read-more');

		for (const element of readMoreElements) {
			new ReadMore(element);
		}
	}

	async initImageSwap () {
		// Elements
		const imageSwapElements = document.getElementsByClassName('js-image-swap');

		if (imageSwapElements.length === 0) {
			return;
		}

		// Module
		const {default: ImageSwap} = await import('custom-extensions/image-swap');

		for (const element of imageSwapElements) {
			new ImageSwap(element);
		}
	}

	async initEmailValidationCheck () {
		const emailElements = document.querySelectorAll('[name="Address_Email"], [name="ShipEmail"], [name="BillEmail"], input#l-Create_Customer_LoginEmail');
		if (!emailElements.length) {
			return;
		}

		// Module
		const {default: EmailSuggestion} = await import('custom-extensions/email-suggestion');
		new EmailSuggestion(emailElements);
	}

	initTicker () {
		const mobileBreakpoint = window.matchMedia('(max-width: 670px)');
		const headerMessage = document.getElementById('header_message');
		let hasCloned = false;

		if (!headerMessage) {
			return;
		}

		const messageSpan = headerMessage.querySelector('.g-site-header__message');

		const setupTicker = (e) => {
			// Only clone if under 670px AND we haven't already done it
			if (e.matches && !hasCloned && messageSpan) {
				const clone = messageSpan.cloneNode(true);
				clone.classList.add('ticker-clone');
				clone.setAttribute('aria-hidden', 'true');

				headerMessage.appendChild(clone);
				hasCloned = true;
			}
		};

		// Check immediately on load
		setupTicker(mobileBreakpoint);

		// Listen for screen resizing
		mobileBreakpoint.addEventListener('change', setupTicker);
	}

}
